import { cn } from "@lib/ui/cn";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
  "underline-fo relative z-10 inline-flex shrink-0 cursor-pointer items-center justify-center gap-3 rounded-md whitespace-nowrap no-underline transition-all outline-none focus:underline disabled:pointer-events-none [&>svg]:shrink-0 [&>svg]:fill-current",
  {
    variants: {
      variant: {
        primary:
          "text-indigo-darkest light:text-white light:hover:text-neutral-darkest light:focus:text-neutral-darkest light:bg-neutral-darkest focus:border-text-active-alternate hover:border-text-active-alternate focus:bg-text-active-alternate hover:bg-text-active-alternate disabled:bg-neutral-light light:disabled:bg-neutral-lighter light:disabled:border-neutral-lighter disabled:text-neutral-dark disabled:border-neutral-light border border-white bg-white",
        secondary:
          "border-neutral focus:text-emerald-light light:text-neutral-darkest light:border-neutral-darkest light:hover:bg-emerald-lightest light:focus:bg-emerald-lightest light:hover:bg-none light:focus:bg-none light:hover:text-emerald-darker light:focus:text-emerald-darker light:hover:border-emerald-darker light:focus:border-emerald-darker focus:border-emerald-darker hover:text-emerald-light hover:border-emerald-darker light:disabled:border-neutral-light light:disabled:text-neutral-light relative border-2 bg-transparent text-white before:absolute before:inset-0 before:rounded-[calc(0.375rem-2px)] before:bg-[linear-gradient(180deg,rgba(144,237,165,0)_37.9%,rgba(32,245,84,0.2)_80.3%)] before:opacity-0 before:transition-opacity hover:before:opacity-100 focus:before:opacity-100 disabled:border-white/40 disabled:text-white/40",
        tertiary:
          "focus:text-emerald-light hover:text-emerald-light light:text-neutral-darkest light:hover:text-emerald-darker light:focus:text-emerald-darker light:disabled:text-neutral-light text-white disabled:text-white/40",
        link: "focus:text-emerald-light hover:text-emerald-light light:text-neutral-darkest light:hover:text-emerald-darker light:focus:text-emerald-darker light:disabled:text-neutral-light gap-2 text-white disabled:text-white/40",
      },
      iconOnly: {
        true: null,
        false: null,
      },
      iconPosition: {
        left: "flex-row",
        right: "flex-row",
        none: null,
      },
      size: {
        default:
          "px-padding-element-lg py-padding-element-md typography-text-medium-medium [&>svg]:size-6",
        sm: "px-padding-element-lg py-padding-element-2xs typography-text-regular-medium [&>svg]:size-5",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      iconOnly: false,
      iconPosition: "none",
    },
    compoundVariants: [
      {
        variant: "link",
        size: ["default", "sm"],
        className: "px-0! py-0!",
      },
      {
        iconOnly: true,
        size: "default",
        className: "px-padding-element-md py-padding-element-md",
      },
      {
        iconOnly: true,
        size: "sm",
        className: "px-padding-element-3xs! py-padding-element-3xs! [&>svg]:size-6",
      },
      {
        iconPosition: "left",
        iconOnly: false,
        className: "[&>svg:first-child]:order-first",
      },
      {
        iconPosition: "right",
        iconOnly: false,
        className: "[&>svg:last-child]:order-last",
      },
    ],
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
}

function Button({
  className,
  variant,
  size,
  iconOnly,
  iconPosition,
  icon,
  children,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  // Determine if button has icon
  const hasIcon = Boolean(icon);
  const hasText = Boolean(children);
  const isIconOnly = iconOnly || (hasIcon && !hasText);

  // Determine icon position
  // For icon-only buttons, always render the icon (position doesn't matter)
  // For buttons with text, default to right if no position specified
  const resolvedIconPosition = isIconOnly
    ? "none" // Position doesn't matter for icon-only, but we'll render it anyway
    : iconPosition || (hasIcon && hasText ? "right" : "none");

  // When using asChild, Slot requires a single child element
  // We need to clone the child and include the icon within it
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement;
    const iconContent = isIconOnly ? (
      // Icon-only: just render the icon
      icon
    ) : (
      // Button with text: render icon based on position
      <>
        {resolvedIconPosition === "left" && icon}
        {child.props.children}
        {resolvedIconPosition === "right" && icon}
      </>
    );

    return (
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({
            variant,
            size,
            iconOnly: isIconOnly,
            iconPosition: resolvedIconPosition,
          }),
          className,
        )}
        {...props}
      >
        {React.cloneElement(child, {
          ...child.props,
          children: iconContent,
        })}
      </Comp>
    );
  }

  // Regular button rendering (not asChild)
  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({
          variant,
          size,
          iconOnly: isIconOnly,
          iconPosition: resolvedIconPosition,
        }),
        className,
      )}
      {...props}
    >
      {isIconOnly ? (
        // Icon-only: always render the icon
        icon
      ) : (
        // Button with text: render icon based on position
        <>
          {resolvedIconPosition === "left" && icon}
          {children}
          {resolvedIconPosition === "right" && icon}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
